import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { dateConfig, debounceConfig } from '@app/core/_config/';
import { AuthService } from '@app/core/auth';
import { FactoryService } from '@app/core/factories';
import { DefectiveReportService } from '@app/core/purchase-orders/services/defective-report.service';
import { PoService } from '@app/core/purchase-orders/services/po.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { UIStateService } from '@app/shared/services/ui-state.service';
import { trimWala } from '@app/shared/utils/string.utils';

import { DefectiveReportsFormComponent } from '../defective-reports-form/defective-reports-form.component';

@Component({
  selector: 'cnc-defective-reports',
  templateUrl: './defective-reports.component.html',
  styleUrls: ['./defective-reports.component.scss'],
})
export class DefectiveReportsComponent implements OnInit {
  url :string = environment.url;

  ordersRecord :any = { data: [] };
  pageSizeOptions :number[] = [5, 10, 25, 100];
  minDate :string = dateConfig.limit.min;
  maxDate :string = dateConfig.limit.max;
  debounceTime :number = debounceConfig.time;
  ftLocationBins :boolean = false;
  ftLotTracking :boolean = false;

  totals :any;
  filter :any = {
    po_number: '',
    date: moment().subtract(30, 'days').format('yyyy-MM-DD'),
    date_to: moment().format('yyyy-MM-DD'),
  };
  params :any = {
    per_page: 20,
    page: 1,
  };

  poSubscription :Subscription;

  constructor(
    public defectiveReportSvc :DefectiveReportService,
    public factorySvc :FactoryService,
    private cd :ChangeDetectorRef,
    private modalSvc :NgbModal,
    private translate :TranslateService,
    private toastr :ToastrService,
    private poSvc :PoService,
    public authService :AuthService,
    private uiStateSvc :UIStateService,
    private ft :FeatureToggleService,
  ) {
    this.ftLotTracking = this.ft.featureEnabled(EFeatureFlag.LOT_TRACKING);
  }

  ngOnInit() {
    this.setFiltersOnInit();
    this.getRecords();

    this.poSubscription = this.defectiveReportSvc.defectiveReports$.subscribe(
      (records) => {
        this.ordersRecord = records;
        this.cd.detectChanges();
      },
    );
  }

  openDefectiveForm(defectiveReport ?:any) {
    const modalRef = this.modalSvc.open(DefectiveReportsFormComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal xl1024',
    });
    if (defectiveReport) {
      modalRef.componentInstance.ftLocationBins = this.ftLocationBins;
      modalRef.componentInstance.ftLotTracking = this.ftLotTracking;
      modalRef.componentInstance.defectiveReport = defectiveReport;
    }
    modalRef.result.then(
      (record :any) => {
        if (defectiveReport && record) {
          this.processTotals(record, defectiveReport);
        } else {
          this.totals.quantity += record.total_quantity;
          this.totals.total += record.total_cost;
        }
        this.getRecords(false, 1);
      },
      (_) => {},
    );
  }

  private processTotals(newData :any, oldData :any) :void {
    let differenceQty :number = 0;
    let differenceCost :number = 0;
    //Quantity
    if (newData.total_quantity > oldData.total_quantity) {
      differenceQty = newData.total_quantity - oldData.total_quantity;
      this.totals.quantity += differenceQty;
    } else if (newData.total_quantity < oldData.total_quantity) {
      differenceQty = oldData.total_quantity - newData.total_quantity;
      this.totals.quantity -= differenceQty;
    }

    //Cost
    if (newData.total_cost > oldData.total_cost) {
      differenceCost = newData.total_cost - oldData.total_cost;
      this.totals.total += differenceCost;
    } else if (newData.total_cost < oldData.total_cost) {
      differenceCost = oldData.total_cost - newData.total_cost;
      this.totals.total -= differenceCost;
    }
    this.cd.detectChanges();
  }

  deleteDefectiveReport(defectiveReport :any) {
    Swal.fire({
      title: 'Confirm Delete?',
      text: 'Delete purchase order defective report?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.defectiveReportSvc
          .deleteDefectiveReport(defectiveReport.id)
          .subscribe((resp :any) => {
            this.totals.total = this.totals.total - defectiveReport.total_cost;
            this.totals.quantity =
              this.totals.quantity - defectiveReport.total_quantity;
            this.toastr.success(
              'Successfully deleted purchase order defective report.',
              'Success',
            );
            this.cd.detectChanges();
          });
      }
    });
  }
  getRecords(event ?:any, page ?:number, paramsOnly ?:boolean) {
    if (page) {
      this.params.page = page;
    }

    this.params = Object.assign(this.params, this.filter);

    if (this.params.order) {
      this.params.order = trimWala(this.params.order, '-');
    }
    if (this.params.po_number) {
      this.params.po_number = this.poSvc.getPoNumberFromPOString(
        this.params.po_number,
      );
    }
    if (!this.params.defect_type || this.params.defect_type == 0) {
      delete this.params.defect_type;
    }
    if (event) {
      this.params.page = event.pageIndex + 1;
      this.params.per_page = event.pageSize;
    }

    if (!this.params.factory_id) {
      delete this.params.factory_id;
    }

    if (!this.params.po_number) {
      delete this.params.po_number;
    }

    if (!this.params.sku) {
      delete this.params.sku;
    }

    if (!this.params.date) {
      delete this.params.date;
    }

    if (!this.params.date_to) {
      delete this.params.date_to;
    }

    // set state
    if (this.params.page == 1) {
      this.defectiveReportSvc.getTotalAsync(this.params).subscribe((totals) => {
        this.totals = totals;
      });
    }

    if (this.params.total) {
      delete this.params.total;
    }

    if (paramsOnly) {
      return this.params;
    }

    this.uiStateSvc.setState('purchaseOrdersDefectiveReports', this.params);
    this.defectiveReportSvc.get(this.params);

    const element = document.getElementById('scroll-top-pagination');
    element?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  ngOnDestroy() {
    this.poSubscription.unsubscribe();
  }

  resetFilters() {
    this.filter = {};
    delete this.params.date;
    delete this.params.date_to;
    delete this.params.sku;
    delete this.params.po_number;
    delete this.params.defect_type;
    if (this.params?.all) {
      delete this.params?.all;
    }
    this.getRecords();
  }

  getDefectType(defect_type :any) {
    const dt = this.defectiveReportSvc.defectTypes.find(
      (row) => row.id == defect_type,
    );

    return dt ? dt.label : '';
  }

  exportListToPdf() {
    Swal.fire('Exporting to PDF, please wait...');
    Swal.showLoading();
    const params = this.getRecords(false, 1, true);
    params.all = 1;

    this.defectiveReportSvc.getAsync(params).subscribe((resp) => {
      this.defectiveReportSvc.exportListtoPDF(resp);
      delete params.all;
    });
  }

  exportListToCsv() {
    Swal.fire('Exporting to CSV, please wait...');
    Swal.showLoading();

    const params = this.getRecords(false, 1, true);
    params.all = 1;

    this.defectiveReportSvc.getAsync(params).subscribe((resp) => {
      this.defectiveReportSvc.exportListtoCSV(resp);
      delete params.all;
    });
  }

  validateDate(prop :string) :void {
    const min = moment(dateConfig.limit.min);
    const max = moment(dateConfig.limit.max);

    if (this.filter.hasOwnProperty(prop)) {
      const inputDate = moment(this.filter[prop]);

      if (inputDate.isValid()) {
        if (inputDate.isBefore(min)) {
          this.filter[prop] = inputDate
            .set('year', min.year())
            .format('YYYY-MM-DD');
        }

        if (inputDate.isAfter(max)) {
          this.filter[prop] = inputDate
            .set('year', max.year())
            .format('YYYY-MM-DD');
        }
      } else {
        this.filter[prop] = min.format('YYYY-MM-DD');
      }

      this.getRecords(false, 1);
    }
  }

  setFiltersOnInit() :void {
    const stateView = this.uiStateSvc.getState('purchaseOrdersDefectiveReports');
    if (stateView) {
      this.filter = stateView;
    }
  }
}
